import React, { useState } from 'react';
import axios from 'axios';
import { X, DollarSign } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'wire', label: 'Wire / Zelle' },
  { value: 'other', label: 'Other' },
];

function MarkPaidDialog({ isOpen, onClose, intake, onSuccess }) {
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen || !intake) return null;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/intake/${intake.id}/mark-paid`,
        {
          payment_method: paymentMethod,
          reference_number: referenceNumber.trim() || null,
          notes: notes.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPaymentMethod('cash');
      setReferenceNumber('');
      setNotes('');
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Error marking paid:', err);
      setError(err.response?.data?.detail || 'Failed to mark as paid');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
        <div className="fixed inset-0 bg-black bg-opacity-75 transition-opacity" onClick={onClose} />
        <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-virgo-navy-800 border border-virgo-teal-700 shadow-xl rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-virgo-gold-400 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-400" />
              Mark Consultation Fee Paid
            </h3>
            <button onClick={onClose} className="text-virgo-teal-400 hover:text-virgo-teal-300">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mb-4 p-3 bg-virgo-navy-700 rounded-lg">
            <p className="text-white font-medium">
              {intake.first_name} {intake.last_name}
            </p>
            <p className="text-virgo-teal-400 text-sm">
              Fee: ${((intake.consult_fee || 15000) / 100).toFixed(2)}
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                Payment Method *
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500 focus:border-virgo-teal-500"
              >
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                Reference Number
              </label>
              <input
                type="text"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                placeholder="Check #, confirmation code, etc."
                className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-virgo-teal-500 focus:border-virgo-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes about this payment..."
                rows={2}
                className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-virgo-teal-500 focus:border-virgo-teal-500"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-virgo-teal-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <DollarSign className="w-4 h-4" />
                  Confirm Payment
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MarkPaidDialog;
